import React from "react"
import { useState } from "react"
import { Card, Container, Row, Col, Button, Form, Badge, Modal, Alert } from "react-bootstrap"
import {
  FaUser,
  FaEdit,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaIdCard,
  FaShieldAlt,
  FaHeart,
  FaPills,
  FaExclamationTriangle,
  FaCheckCircle,
  FaSave,
  FaTimes,
  FaCamera,
  FaLock,
} from "react-icons/fa"

export default function PatientProfile() {
  const [isEditing, setIsEditing] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [showAlert, setShowAlert] = useState({ show: false, variant: "", message: "" })

  const [profileData, setProfileData] = useState({
    firstName: "John",
    lastName: "Smith",
    email: "john.smith@email.com",
    phone: "+1 (555) 987-6543",
    dateOfBirth: "1985-03-15",
    gender: "Male",
    address: "123 Main Street",
    city: "New York",
    state: "NY",
    zipCode: "10001",
    emergencyContact: "Jane Smith",
    emergencyPhone: "+1 (555) 987-6544",
    relationship: "Spouse",
    bloodType: "O+",
    allergies: ["Penicillin", "Shellfish"],
    medications: ["Lisinopril 10mg", "Metformin 500mg"],
    insurance: "Blue Cross Blue Shield",
    policyNumber: "BC123456789",
    primaryDoctor: "Dr. Michael Chen",
  })

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  })

  const handleInputChange = (field, value) => {
    setProfileData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleSave = () => {
    setShowAlert({
      show: true,
      variant: "success",
      message: "Profile updated successfully!",
    })
    setIsEditing(false)
    setTimeout(() => setShowAlert({ show: false, variant: "", message: "" }), 3000)
  }

  const handlePasswordChange = () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setShowAlert({
        show: true,
        variant: "danger",
        message: "New passwords do not match!",
      })
      return
    }

    setShowAlert({
      show: true,
      variant: "success",
      message: "Password changed successfully!",
    })
    setShowPasswordModal(false)
    setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" })
    setTimeout(() => setShowAlert({ show: false, variant: "", message: "" }), 3000)
  }

  return (
    <div className="min-vh-100" style={{ backgroundColor: "#f8fffe" }}>
      <Container className="py-5">
        {/* Header */}
        <div className="mb-5">
          <div
            className="p-5 rounded-4 text-white position-relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
            }}
          >
            {/* Background Pattern */}
            <div className="position-absolute w-100 h-100 top-0 start-0">
              <div
                className="position-absolute rounded-circle opacity-10"
                style={{
                  width: "200px",
                  height: "200px",
                  background: "white",
                  top: "-50px",
                  right: "-50px",
                }}
              ></div>
            </div>

            <Row className="align-items-center position-relative">
              <Col md={8}>
                <div className="mb-3">
                  <Badge
                    className="px-3 py-2 rounded-pill fw-medium mb-3"
                    style={{
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                    }}
                  >
                    <FaUser className="me-2" />
                    Patient Profile
                  </Badge>
                </div>
                <h1 className="display-5 fw-bold mb-3">My Profile</h1>
                <p className="fs-5 opacity-90 mb-4">Manage your personal information and medical details</p>
                <div className="d-flex gap-3">
                  {!isEditing ? (
                    <Button
                      variant="light"
                      className="px-4 py-2 rounded-pill fw-medium"
                      onClick={() => setIsEditing(true)}
                      style={{
                        boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
                      }}
                    >
                      <FaEdit className="me-2" style={{ color: "#0891b2" }} />
                      <span style={{ color: "#0891b2" }}>Edit Profile</span>
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="light"
                        className="px-4 py-2 rounded-pill fw-medium"
                        onClick={handleSave}
                        style={{
                          boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
                        }}
                      >
                        <FaSave className="me-2" style={{ color: "#059669" }} />
                        <span style={{ color: "#059669" }}>Save Changes</span>
                      </Button>
                      <Button
                        variant="outline-light"
                        className="px-4 py-2 rounded-pill fw-medium"
                        onClick={() => setIsEditing(false)}
                        style={{
                          borderWidth: "2px",
                          backdropFilter: "blur(10px)",
                          backgroundColor: "rgba(255, 255, 255, 0.1)",
                        }}
                      >
                        <FaTimes className="me-2" />
                        Cancel
                      </Button>
                    </>
                  )}
                </div>
              </Col>
              <Col md={4} className="text-end">
                <div className="position-relative d-inline-block">
                  <div
                    className="d-inline-flex align-items-center justify-content-center rounded-circle"
                    style={{
                      width: "120px",
                      height: "120px",
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      backdropFilter: "blur(20px)",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                    }}
                  >
                    <FaUser size={48} />
                  </div>
                  {isEditing && (
                    <Button
                      size="sm"
                      className="position-absolute bottom-0 end-0 rounded-circle"
                      style={{
                        width: "35px",
                        height: "35px",
                        background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                        border: "3px solid white",
                      }}
                    >
                      <FaCamera size={14} />
                    </Button>
                  )}
                </div>
              </Col>
            </Row>
          </div>
        </div>

        {showAlert.show && (
          <Alert variant={showAlert.variant} className="mb-4 rounded-4">
            <div className="d-flex align-items-center">
              {showAlert.variant === "success" ? (
                <FaCheckCircle className="me-2" />
              ) : (
                <FaExclamationTriangle className="me-2" />
              )}
              {showAlert.message}
            </div>
          </Alert>
        )}

        <Row className="g-4">
          {/* Personal Information */}
          <Col lg={8}>
            <Card className="border-0 rounded-4 mb-4" style={{ boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08)" }}>
              <Card.Header className="bg-transparent border-0 p-4">
                <div className="d-flex align-items-center">
                  <div
                    className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                    style={{
                      width: "40px",
                      height: "40px",
                      background: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
                      color: "white",
                    }}
                  >
                    <FaUser size={18} />
                  </div>
                  <div>
                    <h5 className="fw-bold mb-1" style={{ color: "#1f2937" }}>
                      Personal Information
                    </h5>
                    <p className="text-muted mb-0">Your basic personal details</p>
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="p-4">
                <Row className="g-4">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-medium text-dark">First Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={profileData.firstName}
                        onChange={(e) => handleInputChange("firstName", e.target.value)}
                        disabled={!isEditing}
                        className="rounded-3 border-0"
                        style={{
                          backgroundColor: isEditing ? "#fff" : "#f0fdfa",
                          boxShadow: isEditing ? "0 0 0 1px #a7f3d0" : "none",
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-medium text-dark">Last Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={profileData.lastName}
                        onChange={(e) => handleInputChange("lastName", e.target.value)}
                        disabled={!isEditing}
                        className="rounded-3 border-0"
                        style={{
                          backgroundColor: isEditing ? "#fff" : "#f0fdfa",
                          boxShadow: isEditing ? "0 0 0 1px #a7f3d0" : "none",
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-medium text-dark">Email Address</Form.Label>
                      <div className="position-relative">
                        <FaEnvelope className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                        <Form.Control
                          type="email"
                          value={profileData.email}
                          onChange={(e) => handleInputChange("email", e.target.value)}
                          disabled={!isEditing}
                          className="ps-5 rounded-3 border-0"
                          style={{
                            backgroundColor: isEditing ? "#fff" : "#f0fdfa",
                            boxShadow: isEditing ? "0 0 0 1px #a7f3d0" : "none",
                          }}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-medium text-dark">Phone Number</Form.Label>
                      <div className="position-relative">
                        <FaPhone className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                        <Form.Control
                          type="tel"
                          value={profileData.phone}
                          onChange={(e) => handleInputChange("phone", e.target.value)}
                          disabled={!isEditing}
                          className="ps-5 rounded-3 border-0"
                          style={{
                            backgroundColor: isEditing ? "#fff" : "#f0fdfa",
                            boxShadow: isEditing ? "0 0 0 1px #a7f3d0" : "none",
                          }}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-medium text-dark">Date of Birth</Form.Label>
                      <div className="position-relative">
                        <FaCalendarAlt className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                        <Form.Control
                          type="date"
                          value={profileData.dateOfBirth}
                          onChange={(e) => handleInputChange("dateOfBirth", e.target.value)}
                          disabled={!isEditing}
                          className="ps-5 rounded-3 border-0"
                          style={{
                            backgroundColor: isEditing ? "#fff" : "#f0fdfa",
                            boxShadow: isEditing ? "0 0 0 1px #a7f3d0" : "none",
                          }}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-medium text-dark">Gender</Form.Label>
                      <Form.Select
                        value={profileData.gender}
                        onChange={(e) => handleInputChange("gender", e.target.value)}
                        disabled={!isEditing}
                        className="rounded-3 border-0"
                        style={{
                          backgroundColor: isEditing ? "#fff" : "#f0fdfa",
                          boxShadow: isEditing ? "0 0 0 1px #a7f3d0" : "none",
                        }}
                      >
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                        <option value="Prefer not to say">Prefer not to say</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Address Information */}
            <Card className="border-0 rounded-4 mb-4" style={{ boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08)" }}>
              <Card.Header className="bg-transparent border-0 p-4">
                <div className="d-flex align-items-center">
                  <div
                    className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                    style={{
                      width: "40px",
                      height: "40px",
                      background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                      color: "white",
                    }}
                  >
                    <FaMapMarkerAlt size={18} />
                  </div>
                  <div>
                    <h5 className="fw-bold mb-1" style={{ color: "#1f2937" }}>
                      Address Information
                    </h5>
                    <p className="text-muted mb-0">Your residential address</p>
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="p-4">
                <Row className="g-4">
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-medium text-dark">Street Address</Form.Label>
                      <Form.Control
                        type="text"
                        value={profileData.address}
                        onChange={(e) => handleInputChange("address", e.target.value)}
                        disabled={!isEditing}
                        className="rounded-3 border-0"
                        style={{
                          backgroundColor: isEditing ? "#fff" : "#f0fdfa",
                          boxShadow: isEditing ? "0 0 0 1px #a7f3d0" : "none",
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-medium text-dark">City</Form.Label>
                      <Form.Control
                        type="text"
                        value={profileData.city}
                        onChange={(e) => handleInputChange("city", e.target.value)}
                        disabled={!isEditing}
                        className="rounded-3 border-0"
                        style={{
                          backgroundColor: isEditing ? "#fff" : "#f0fdfa",
                          boxShadow: isEditing ? "0 0 0 1px #a7f3d0" : "none",
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-medium text-dark">State</Form.Label>
                      <Form.Control
                        type="text"
                        value={profileData.state}
                        onChange={(e) => handleInputChange("state", e.target.value)}
                        disabled={!isEditing}
                        className="rounded-3 border-0"
                        style={{
                          backgroundColor: isEditing ? "#fff" : "#f0fdfa",
                          boxShadow: isEditing ? "0 0 0 1px #a7f3d0" : "none",
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-medium text-dark">ZIP Code</Form.Label>
                      <Form.Control
                        type="text"
                        value={profileData.zipCode}
                        onChange={(e) => handleInputChange("zipCode", e.target.value)}
                        disabled={!isEditing}
                        className="rounded-3 border-0"
                        style={{
                          backgroundColor: isEditing ? "#fff" : "#f0fdfa",
                          boxShadow: isEditing ? "0 0 0 1px #a7f3d0" : "none",
                        }}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Emergency Contact */}
            <Card className="border-0 rounded-4" style={{ boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08)" }}>
              <Card.Header className="bg-transparent border-0 p-4">
                <div className="d-flex align-items-center">
                  <div
                    className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                    style={{
                      width: "40px",
                      height: "40px",
                      background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                      color: "white",
                    }}
                  >
                    <FaPhone size={18} />
                  </div>
                  <div>
                    <h5 className="fw-bold mb-1" style={{ color: "#1f2937" }}>
                      Emergency Contact
                    </h5>
                    <p className="text-muted mb-0">Person to contact in case of emergency</p>
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="p-4">
                <Row className="g-4">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-medium text-dark">Contact Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={profileData.emergencyContact}
                        onChange={(e) => handleInputChange("emergencyContact", e.target.value)}
                        disabled={!isEditing}
                        className="rounded-3 border-0"
                        style={{
                          backgroundColor: isEditing ? "#fff" : "#f0fdfa",
                          boxShadow: isEditing ? "0 0 0 1px #a7f3d0" : "none",
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-medium text-dark">Phone Number</Form.Label>
                      <Form.Control
                        type="tel"
                        value={profileData.emergencyPhone}
                        onChange={(e) => handleInputChange("emergencyPhone", e.target.value)}
                        disabled={!isEditing}
                        className="rounded-3 border-0"
                        style={{
                          backgroundColor: isEditing ? "#fff" : "#f0fdfa",
                          boxShadow: isEditing ? "0 0 0 1px #a7f3d0" : "none",
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-medium text-dark">Relationship</Form.Label>
                      <Form.Select
                        value={profileData.relationship}
                        onChange={(e) => handleInputChange("relationship", e.target.value)}
                        disabled={!isEditing}
                        className="rounded-3 border-0"
                        style={{
                          backgroundColor: isEditing ? "#fff" : "#f0fdfa",
                          boxShadow: isEditing ? "0 0 0 1px #a7f3d0" : "none",
                        }}
                      >
                        <option value="Spouse">Spouse</option>
                        <option value="Parent">Parent</option>
                        <option value="Child">Child</option>
                        <option value="Sibling">Sibling</option>
                        <option value="Friend">Friend</option>
                        <option value="Other">Other</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          {/* Medical Information */}
          <Col lg={4}>
            <Card className="border-0 rounded-4 mb-4" style={{ boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08)" }}>
              <Card.Header className="bg-transparent border-0 p-4">
                <div className="d-flex align-items-center">
                  <div
                    className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                    style={{
                      width: "40px",
                      height: "40px",
                      background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                      color: "white",
                    }}
                  >
                    <FaHeart size={18} />
                  </div>
                  <div>
                    <h5 className="fw-bold mb-1" style={{ color: "#1f2937" }}>
                      Medical Information
                    </h5>
                    <p className="text-muted mb-0">Your health details</p>
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="p-4">
                <div className="mb-4">
                  <Form.Label className="fw-medium text-dark">Blood Type</Form.Label>
                  <div className="mt-2">
                    <Badge
                      className="px-3 py-2 fs-6"
                      style={{
                        background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                        border: "none",
                      }}
                    >
                      {profileData.bloodType}
                    </Badge>
                  </div>
                </div>

                <div className="mb-4">
                  <Form.Label className="fw-medium text-dark">Allergies</Form.Label>
                  <div className="mt-2 d-flex flex-wrap gap-2">
                    {profileData.allergies.map((allergy, index) => (
                      <Badge key={index} style={{ backgroundColor: "#f59e0b" }} className="px-3 py-2">
                        <FaExclamationTriangle className="me-1" size={12} />
                        {allergy}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <Form.Label className="fw-medium text-dark">Current Medications</Form.Label>
                  <div className="mt-2 d-flex flex-column gap-2">
                    {profileData.medications.map((medication, index) => (
                      <div
                        key={index}
                        className="p-2 rounded-3 d-flex align-items-center"
                        style={{ backgroundColor: "#f0fdfa" }}
                      >
                        <FaPills style={{ color: "#0891b2" }} className="me-2" size={14} />
                        <span className="small">{medication}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Form.Label className="fw-medium text-dark">Primary Doctor</Form.Label>
                  <div className="mt-2">
                    <div className="p-3 rounded-3 d-flex align-items-center" style={{ backgroundColor: "#f0fdfa" }}>
                      <div
                        className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                        style={{
                          width: "35px",
                          height: "35px",
                          background: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
                          color: "white",
                        }}
                      >
                        <FaUser size={14} />
                      </div>
                      <span className="fw-medium">{profileData.primaryDoctor}</span>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Insurance Information */}
            <Card className="border-0 rounded-4 mb-4" style={{ boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08)" }}>
              <Card.Header className="bg-transparent border-0 p-4">
                <div className="d-flex align-items-center">
                  <div
                    className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                    style={{
                      width: "40px",
                      height: "40px",
                      background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                      color: "white",
                    }}
                  >
                    <FaShieldAlt size={18} />
                  </div>
                  <div>
                    <h5 className="fw-bold mb-1" style={{ color: "#1f2937" }}>
                      Insurance
                    </h5>
                    <p className="text-muted mb-0">Your insurance details</p>
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="p-4">
                <div className="mb-3">
                  <Form.Label className="fw-medium text-dark">Insurance Provider</Form.Label>
                  <Form.Control
                    type="text"
                    value={profileData.insurance}
                    onChange={(e) => handleInputChange("insurance", e.target.value)}
                    disabled={!isEditing}
                    className="rounded-3 border-0"
                    style={{
                      backgroundColor: isEditing ? "#fff" : "#f0fdfa",
                      boxShadow: isEditing ? "0 0 0 1px #a7f3d0" : "none",
                    }}
                  />
                </div>
                <div>
                  <Form.Label className="fw-medium text-dark">Policy Number</Form.Label>
                  <div className="position-relative">
                    <FaIdCard className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                    <Form.Control
                      type="text"
                      value={profileData.policyNumber}
                      onChange={(e) => handleInputChange("policyNumber", e.target.value)}
                      disabled={!isEditing}
                      className="ps-5 rounded-3 border-0"
                      style={{
                        backgroundColor: isEditing ? "#fff" : "#f0fdfa",
                        boxShadow: isEditing ? "0 0 0 1px #a7f3d0" : "none",
                      }}
                    />
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Security */}
            <Card className="border-0 rounded-4" style={{ boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08)" }}>
              <Card.Header className="bg-transparent border-0 p-4">
                <div className="d-flex align-items-center">
                  <div
                    className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                    style={{
                      width: "40px",
                      height: "40px",
                      background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                      color: "white",
                    }}
                  >
                    <FaLock size={18} />
                  </div>
                  <div>
                    <h5 className="fw-bold mb-1" style={{ color: "#1f2937" }}>
                      Security
                    </h5>
                    <p className="text-muted mb-0">Account security settings</p>
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="p-4">
                <Button
                  variant="outline-primary"
                  className="w-100 rounded-pill"
                  onClick={() => setShowPasswordModal(true)}
                  style={{ borderColor: "#0891b2", color: "#0891b2" }}
                >
                  <FaLock className="me-2" />
                  Change Password
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Password Change Modal */}
        <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)} centered>
          <Modal.Header closeButton className="border-0">
            <Modal.Title>Change Password</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Current Password</Form.Label>
                <Form.Control
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData((prev) => ({ ...prev, currentPassword: e.target.value }))}
                  className="rounded-3"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>New Password</Form.Label>
                <Form.Control
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData((prev) => ({ ...prev, newPassword: e.target.value }))}
                  className="rounded-3"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Confirm New Password</Form.Label>
                <Form.Control
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                  className="rounded-3"
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button variant="outline-secondary" onClick={() => setShowPasswordModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handlePasswordChange}
              style={{
                background: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
                border: "none",
              }}
            >
              Change Password
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  )
}
